import { useEffect, useState } from "react"

import { useInterval } from "react-use"

import { useForm } from "@tanstack/react-form"

import { Upload } from "lucide-react"

import { Field } from "@/components/ui/Field"
import { Textarea } from "@/components/ui/Textarea"
import { Button } from "@/components/ui/Button"

import { UploadingOverlay } from "../overlay/Uploading"

import type { UploadedFileInfo } from "../types"

interface PracticeTopicStepProps {
  value: string
  onChange: (value: string) => void
  onValidation?: (isValid: boolean) => void
  onFileUpload?: () => void
  isUploadingFile?: boolean
  uploadedFiles?: UploadedFileInfo[]
}

const UPLOAD_MESSAGES = [
  "Uploading files...",
  "Extracting content...",
  "Analyzing materials...",
  "Processing files...",
  "Generating content...",
]

export function PracticeTopicStep({
  value,
  onChange,
  onValidation,
  onFileUpload,
  isUploadingFile = false,
  uploadedFiles = [],
}: PracticeTopicStepProps) {
  const form = useForm({
    defaultValues: {
      practiceTopic: value,
    },
  })

  const [uploadMessageIndex, setUploadMessageIndex] = useState(0)

  // Interval for cycling upload messages
  useInterval(
    () => {
      setUploadMessageIndex((prev) => {
        if (prev < UPLOAD_MESSAGES.length - 1) {
          return prev + 1
        }
        return prev
      })
    },
    isUploadingFile ? 1500 : null,
  )

  // Reset state when upload starts/stops
  useEffect(() => {
    if (!isUploadingFile) {
      setUploadMessageIndex(0)
    }
  }, [isUploadingFile])

  return (
    <div className="tw-text-center tw-space-y-6 tw-relative">
      {/* Loading overlay */}
      {isUploadingFile && (
        <UploadingOverlay
          currentMessageIndex={uploadMessageIndex}
          messages={UPLOAD_MESSAGES}
        />
      )}

      <h2 className="tw-text-3xl tw-pb-3 tw-font-bold tw-text-gray-800">
        What are students learning today?
      </h2>

      <span className="tw-text-sm tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-block">
        <span className="tw-font-semibold">💡 Tip:</span> Enter what should
        students practice? You can include standards, difficulty level,
        language, and question types.
      </span>

      <form.Field name="practiceTopic">
        {(field) => (
          <Field>
            <Textarea
              id={field.name}
              value={field.state.value}
              onBlur={() => {
                field.handleBlur()
                onChange(field.state.value)
              }}
              onChange={(e) => {
                field.handleChange(e.target.value)
                onChange(e.target.value)
              }}
              placeholder="e.g: 20 problems on equivalent ratios (CCSS.Math.Content.6.RP.A.3.a), progressively harder, in English and Spanish, mix of multiple choice and open-ended"
              rows={6}
              className="tw-w-full tw-max-w-3xl tw-mx-auto tw-px-6 tw-py-4 tw-border-2 tw-border-dashed tw-rounded-sm tw-text-center tw-text-base tw-text-gray-800 placeholder:tw-text-gray-400 placeholder:tw-whitespace-pre-line tw-transition-all tw-resize-none focus:tw-outline-none focus:tw-ring-2 tw-border-emerald-600 focus:tw-border-emerald-600 focus:tw-ring-emerald-600"
              disabled={isUploadingFile}
            />
          </Field>
        )}
      </form.Field>

      <div className="tw-flex tw-items-center tw-mx-auto tw-w-full tw-max-w-sm tw-gap-4">
        <div className="tw-flex-1 tw-h-px tw-bg-gray-300"></div>
        <span className="tw-text-gray-500 tw-font-medium tw-text-sm">OR</span>
        <div className="tw-flex-1 tw-h-px tw-bg-gray-300"></div>
      </div>

      <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
        <p className="tw-text-sm tw-text-gray-600 ">
          📄 Upload lecture notes, worksheets, or textbook pages to auto fill
          steps for assignment creation.
        </p>
        <Button
          onClick={onFileUpload}
          disabled={isUploadingFile}
          size="lg"
          type="button"
          variant="outline_grey"
          className="tw-min-w-[120px] tw-rounded-full tw-text-sm"
        >
          <Upload className="tw-w-5 tw-h-5" />
          Upload Files (Up to 10 files)
        </Button>
        <p className="tw-text-xs tw-text-gray-500">
          PDF, JPG, PNG • Max 10MB per file
        </p>
      </div>
    </div>
  )
}
